import React, { useState, useContext, useEffect } from 'react';
import { Box, Typography, Button, Dialog } from '@mui/material';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import LockOpenOutlinedIcon from '@mui/icons-material/LockOpenOutlined';
import { styled } from '@mui/system';
import { useNavigate } from 'react-router-dom';
import { ReactComponent as CetraLogo } from '../../../assets/icons/CetraLogo.svg';
import { ReactComponent as LogoCircle } from '../../../assets/icons/LogoCircle.svg';
import BriefcaseIcon from '../../../assets/icons/Briefcase.svg';
import ClinicIcon from '../../../assets/icons/Clinic.svg';
import subscriptionPlans from '../../authenticated/settings/subscriptionPlans.js';
import StoreContext from '../../../react-core-lib/components/app/store-context.js';
import getDiscountByPlan from '../../helper/getDiscountHelper.js';
import KlaviyoApi from '../../../rest-api/klaviyo-api';
import UserApi from '../../../rest-api/user-api';
import BlockingSpinner from '../../UIControls/BlockingSpinner';
import PaymentApi from '../../../rest-api/payment-api';
import mixpanel from 'mixpanel-browser';
import { useSnackbar } from '../../authenticated/SnackbarProvider';

const PLAN_DISPLAY_NAMES = {
    Free: 'Free',
    Professional: 'Pro',
    Clinic: 'Clinic',
};

const PLAN_ICONS = {
    Free: LockOpenOutlinedIcon,
};

const StyledToggleButton = styled(Button)(({ selected }) => ({
    flex: 1,
    textTransform: 'none',
    fontWeight: 500,
    fontSize: '16px',
    borderRadius: '50px',
    padding: '8px 24px',
    border: 'none',
    color: selected ? '#fff' : '#000453',
    backgroundColor: selected ? '#000453' : 'transparent',
    '&:hover': {
        backgroundColor: selected ? '#000453' : 'rgba(0,4,83,0.06)',
    },
    minWidth: 100,
    lineHeight: 1.5,
}));

const OnboardPlanScreen = () => {
    const navigate = useNavigate();
    const planKeys = Object.keys(subscriptionPlans).filter((p) => !subscriptionPlans[p].legacy);
    const { authStore, userDataStore } = useContext(StoreContext);
    const showSnackbar = useSnackbar();
    const isMultiUser = userDataStore?.subscriptionData?.accountType === 'Clinic-multiuser';
    const [isProcessing, setIsProcessing] = useState(false);
    const [isHydratingUserData, setIsHydratingUserData] = useState(false);
    const [bootstrapError, setBootstrapError] = useState('');
    const isShowOffer = false;
    const [billingCycle, setBillingCycle] = useState('monthly');
    const [discounts, setDiscounts] = useState({});
    const [enterpriseDialogOpen, setEnterpriseDialogOpen] = useState(false);

    const hasSubData =
        userDataStore?.subscriptionData != null &&
        Object.prototype.hasOwnProperty.call(userDataStore.subscriptionData, 'subscriptionEndDate');
    const isUserDataReady = !!userDataStore?.userData?.emailAddress && hasSubData;

    useEffect(() => {
        localStorage.setItem('onboardingComplete', 'true');
    }, []);

    useEffect(() => {
        const fetchDiscounts = async () => {
            const newDiscounts = {};
            for (const plan of planKeys) {
                const discount = await getDiscountByPlan(authStore?.idToken, plan);
                newDiscounts[plan] = discount ? { type: 'percent', value: discount } : null;
            }
            setDiscounts(newDiscounts);
        };
        fetchDiscounts();
        // planKeys is derived from a static import, safe to omit
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [authStore?.idToken]);

    useEffect(() => {
        if (isUserDataReady || !authStore?.idToken || isHydratingUserData) {
            return;
        }

        let isUnmounted = false;

        const hydrateUserData = async () => {
            setIsHydratingUserData(true);
            setBootstrapError('');

            try {
                const clinicianDataResponse = await UserApi.getClinicianData(authStore.idToken);

                if (!clinicianDataResponse?.userData?.emailAddress) {
                    throw new Error('Unable to load clinician profile data.');
                }

                let isMultiuser = false;
                let userHandouts = clinicianDataResponse.handouts || [];
                let inviteId = '';
                let foundMultiUserData = {};

                if (clinicianDataResponse.multiuserData?.multiuserUsers) {
                    const foundUser = clinicianDataResponse.multiuserData.multiuserUsers.find(
                        (user) => user.userId === userDataStore?.userId,
                    );
                    if (foundUser) {
                        isMultiuser = true;
                        userHandouts = foundUser.handouts || userHandouts;
                        inviteId = foundUser.inviteId || '';
                        foundMultiUserData = foundUser;
                    }
                }

                const formattedData = {
                    firstName: foundMultiUserData.firstName || clinicianDataResponse.userData.firstName || '',
                    lastName: foundMultiUserData.lastName || clinicianDataResponse.userData.lastName || '',
                    emailAddress: foundMultiUserData.emailAddress || clinicianDataResponse.userData.emailAddress || '',
                    mobile: foundMultiUserData.mobile || clinicianDataResponse.userData.mobile || '',
                    clinicName: clinicianDataResponse?.clinicData?.clinicName || '',
                    clinicNumber: clinicianDataResponse?.clinicData?.clinicNumber || '',
                    clinicSize: clinicianDataResponse?.clinicData?.clinicSize || '',
                    clinicImageId: '',
                    showLogoInHandouts: clinicianDataResponse.showLogoInHandouts,
                    handouts: JSON.stringify(userHandouts),
                    recentSearches: isMultiuser
                        ? foundMultiUserData.recentSearches || []
                        : clinicianDataResponse.recentSearches || [],
                    inviteId,
                    whiteboards: isMultiuser
                        ? foundMultiUserData.whiteboards || []
                        : clinicianDataResponse.whiteboards || [],
                    whiteboardsFavourites: isMultiuser
                        ? foundMultiUserData.whiteboardsFavourites || []
                        : clinicianDataResponse.whiteboardsFavourites || [],
                    customisedHandouts: isMultiuser
                        ? foundMultiUserData.customisedHandouts || []
                        : clinicianDataResponse.customisedHandouts || [],
                    customisedHandoutsFavourites: isMultiuser
                        ? foundMultiUserData.customisedHandoutsFavourites || []
                        : clinicianDataResponse.customisedHandoutsFavourites || [],
                    lastUsedColours: isMultiuser
                        ? (foundMultiUserData?.lastUsedColours ??
                          clinicianDataResponse?.lastUsedColours ?? { pencil: [], spray: [] })
                        : (clinicianDataResponse?.lastUsedColours ?? { pencil: [], spray: [] }),
                    subscriptionData: clinicianDataResponse.subscriptionData
                        ? {
                              ...clinicianDataResponse.subscriptionData,
                              accountType: isMultiuser
                                  ? 'Clinic-multiuser'
                                  : clinicianDataResponse.subscriptionData.accountType,
                          }
                        : undefined,
                };

                if (isUnmounted) return;

                userDataStore.setUserData(formattedData);
                await authStore.checkAndSetAuthState(formattedData.emailAddress, authStore.idToken);
            } catch (error) {
                if (isUnmounted) return;
                console.error('Failed to bootstrap user data for plan screen:', error);
                setBootstrapError('Unable to load your account. Please sign in again.');
            } finally {
                if (!isUnmounted) {
                    setIsHydratingUserData(false);
                }
            }
        };

        hydrateUserData();

        return () => {
            isUnmounted = true;
        };
    }, [authStore, isHydratingUserData, isUserDataReady, userDataStore]);

    const getDisplayPrice = (plan) => {
        const planData = subscriptionPlans[plan];
        if (!planData) return '0.00';
        if (billingCycle === 'monthly') {
            return planData.priceMonthly.toFixed(2);
        }
        return planData.priceAnnually > 0 ? planData.priceAnnually.toFixed(2) : '0.00';
    };

    const handleSelectPlan = async (plan) => {
        if (isMultiUser) return;

        // Load the registration payload saved during OTP verification
        const storedPayload = JSON.parse(localStorage.getItem('registrationPayload') || '{}');
        const email = userDataStore.userData.emailAddress;

        if (plan === 'Free') {
            setIsProcessing(true);
            try {
                const freePlan = subscriptionPlans.Free;
                const planDetails = {
                    priceId: billingCycle === 'monthly' ? freePlan.priceMonthlyId : freePlan.priceAnnuallyId,
                    productId: freePlan.productId,
                    isMonthly: billingCycle === 'monthly',
                    accountType: freePlan.accountType,
                };

                const paymentResponse = await PaymentApi.updatePaymentDetails(authStore.idToken, {
                    userId: userDataStore.userId,
                    email,
                    plan: planDetails,
                });

                const subscriptionData = {
                    accountType: 'Free',
                    trialActive: false,
                    trialEndDate: null,
                    subscriptionEndDate: paymentResponse?.subscription?.current_period_end
                        ? paymentResponse.subscription.current_period_end * 1000
                        : null,
                    cancelled: false,
                    // pendingPlan: null,
                    stripeCustomerId:
                        paymentResponse?.customerId || userDataStore?.subscriptionData?.stripeCustomerId || null,
                };

                // Create the clinician record now that we know the plan
                const fullPayload = {
                    ...storedPayload,
                    handouts: [],
                    subscriptionData,
                };
                await UserApi.updateClinicianData(authStore.idToken, fullPayload);

                userDataStore.setUserData({ ...fullPayload });
                authStore.checkAndSetAuthState(email, authStore.idToken);
                await KlaviyoApi.updateUserInKlaviyo(authStore.idToken, email, 'General');
                await KlaviyoApi.updateUserInKlaviyo(authStore.idToken, email, 'Free', {
                    preserveExistingLists: true,
                });
                mixpanel.identify(email);
                mixpanel.track('FreePlanCreated', {
                    userId: userDataStore.userId,
                    email,
                });
                localStorage.removeItem('registrationPayload');
                navigate('/');
            } catch (err) {
                console.error('Error provisioning Free plan:', err);
                showSnackbar(
                    'There was an error setting up your Free plan. Please try again or contact support.',
                    'error',
                );
            } finally {
                setIsProcessing(false);
            }
            return;
        }

        // Pro or Clinic — create the clinician record with a pending subscription, then go to checkout
        setIsProcessing(true);
        try {
            const subscriptionData = {
                accountType: plan,
                trialActive: false,
                trialEndDate: null,
                subscriptionEndDate: null,
                cancelled: false,
                // pendingPlan: null, // DB schema requires object|null — track pending plan in store only
                stripeCustomerId: null,
            };

            const fullPayload = {
                ...storedPayload,
                handouts: [],
                subscriptionData,
            };
            await UserApi.updateClinicianData(authStore.idToken, fullPayload);

            // Store pendingPlan as string in memory only (used by checkout to detect new signup)
            userDataStore.setUserData({ ...fullPayload, subscriptionData: { ...subscriptionData, pendingPlan: plan } });
            await KlaviyoApi.updateUserInKlaviyo(authStore.idToken, email, 'General');
            localStorage.removeItem('registrationPayload');

            const path = isShowOffer ? '/checkout?LVS=true' : '/checkout';
            navigate(path, { state: { userData: storedPayload, plan, billingCycle } });
        } catch (err) {
            console.error('Error creating clinician record:', err);
            showSnackbar('There was an error setting up your account. Please try again or contact support.', 'error');
        } finally {
            setIsProcessing(false);
        }
    };

    if (!isUserDataReady) {
        if (bootstrapError) {
            return (
                <Box
                    sx={{
                        minHeight: '100vh',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        flexDirection: 'column',
                        gap: 2,
                        px: 3,
                        textAlign: 'center',
                    }}
                >
                    <Typography sx={{ color: '#000453', fontSize: 16 }}>{bootstrapError}</Typography>
                    <Button
                        variant="contained"
                        onClick={() => authStore.logout()}
                        sx={{
                            backgroundColor: '#FF669B',
                            color: '#fff',
                            borderRadius: '28px',
                            textTransform: 'none',
                            '&:hover': { backgroundColor: '#e65588' },
                        }}
                    >
                        Back to login
                    </Button>
                </Box>
            );
        }
        return <BlockingSpinner open={true} />;
    }

    return (
        <>
            <BlockingSpinner open={isProcessing} />
            <Box
                sx={{
                    minHeight: '100vh',
                    backgroundColor: '#F5F6FB',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    py: { xs: 4, md: 6 },
                    px: { xs: 2, md: 3 },
                }}
            >
                {/* Cetra logo — same as OTP/register screens */}
                <Box sx={{ mb: { xs: 2, md: 3 } }}>
                    <Box sx={{ position: 'relative', width: { xs: 50, md: 64 }, height: { xs: 50, md: 64 } }}>
                        <Box
                            component={LogoCircle}
                            sx={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%' }}
                        />
                        <Box
                            component={CetraLogo}
                            sx={{
                                position: 'absolute',
                                top: '50%',
                                left: '50%',
                                transform: 'translate(-50%, -50%)',
                                width: { xs: 29, md: 42 },
                                height: { xs: 22, md: 32 },
                            }}
                        />
                    </Box>
                </Box>

                <Box
                    sx={{
                        backgroundColor: '#fff',
                        borderRadius: '30px',
                        border: '1px solid rgba(102, 102, 102, 0.15)',
                        width: '100%',
                        maxWidth: 1245,
                        px: { xs: 3, md: '46px' },
                        py: { xs: 3, md: '40px' },
                    }}
                >
                    {/* Header */}
                    <Box sx={{ textAlign: 'center', mb: 4 }}>
                        {/* Title */}
                        <Typography
                            sx={{
                                fontSize: { xs: '26px', md: '36px' },
                                fontWeight: 600,
                                color: '#000453',
                                letterSpacing: { xs: '-0.52px', md: '-0.72px' },
                                lineHeight: { xs: '26px', md: '36px' },
                                mb: '19px',
                            }}
                        >
                            Choose your plan
                        </Typography>

                        {/* Subtitle */}
                        <Typography
                            sx={{
                                fontSize: { xs: '12px', md: '18px' },
                                fontWeight: 400,
                                color: '#616063',
                                letterSpacing: { xs: '-0.24px', md: '-0.36px' },
                                lineHeight: { xs: '22px', md: '28px' },
                                maxWidth: { xs: 290, md: 749 },
                                margin: '0 auto',
                            }}
                        >
                            Choose the plan that fits your needs. All plans include essential features to get you
                            started, with the flexibility to scale as you grow. No hidden fees and the freedom to change
                            anytime.
                        </Typography>

                        {/* Billing toggle — white bg, #e5e5e5 border, pill shape, height 50px */}
                        <Box
                            sx={{
                                display: 'inline-flex',
                                alignItems: 'center',
                                mt: '32px',
                                backgroundColor: '#ffffff',
                                border: '1px solid #e5e5e5',
                                borderRadius: '58px',
                                p: '4px',
                                gap: '4px',
                                height: '50px',
                                boxSizing: 'border-box',
                            }}
                        >
                            <StyledToggleButton
                                selected={billingCycle === 'monthly' ? 1 : 0}
                                onClick={() => setBillingCycle('monthly')}
                            >
                                Monthly
                            </StyledToggleButton>
                            <StyledToggleButton
                                selected={billingCycle === 'annually' ? 1 : 0}
                                onClick={() => setBillingCycle('annually')}
                            >
                                Annual
                            </StyledToggleButton>
                        </Box>
                    </Box>

                    {/* Multi-user warning */}
                    {isMultiUser && (
                        <Box
                            sx={{
                                backgroundColor: '#f5f5f5',
                                border: '1px solid #ccc',
                                borderRadius: '12px',
                                p: 2,
                                mb: 3,
                            }}
                        >
                            <Typography variant="body2" sx={{ color: '#555' }}>
                                ⚠️ You're currently logged in as a clinic team member. Only the Administrator can manage
                                or change the subscription plan.
                            </Typography>
                        </Box>
                    )}

                    {/* Plan cards — CSS grid, 1 col mobile / 3 col tablet+ */}
                    <Box
                        sx={{
                            display: 'grid',
                            gridTemplateColumns: { xs: '1fr', sm: 'repeat(3, minmax(0, 1fr))' },
                            gap: { xs: '16px', sm: '24px', lg: '46px' },
                        }}
                    >
                        {planKeys
                            .filter((plan) => !subscriptionPlans[plan].legacy)
                            .map((plan) => {
                                const planData = subscriptionPlans[plan];
                                const displayName = PLAN_DISPLAY_NAMES[plan] || plan;
                                const PlanIcon = PLAN_ICONS[plan] || LockOpenOutlinedIcon;
                                const isProfessionalPlan = plan === 'Professional';
                                const isClinicPlan = plan === 'Clinic';
                                const price = getDisplayPrice(plan);
                                const discount = discounts[plan];

                                return (
                                    <Box
                                        key={plan}
                                        sx={{
                                            borderRadius: '30px',
                                            border: '0.5px solid #e5e5e5',
                                            overflow: 'hidden',
                                            display: 'flex',
                                            flexDirection: 'column',
                                        }}
                                    >
                                        {/* Top gray section — icon, name, description, price */}
                                        <Box
                                            sx={{
                                                backgroundColor: '#f0f0fc',
                                                p: 3,
                                                pb: 2.5,
                                            }}
                                        >
                                            {/* Icon */}
                                            <Box
                                                sx={{
                                                    width: 48,
                                                    height: 48,
                                                    backgroundColor: '#fff',
                                                    borderRadius: '12px',
                                                    border: '1px solid rgba(255, 102, 155, 0.35)',
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'center',
                                                    mb: 2,
                                                }}
                                            >
                                                {isProfessionalPlan ? (
                                                    <Box component="img" src={BriefcaseIcon} alt="Briefcase" sx={{ width: 20, height: 20 }} />
                                                ) : isClinicPlan ? (
                                                    <Box component="img" src={ClinicIcon} alt="Clinic" sx={{ width: 20, height: 22 }} />
                                                ) : (
                                                    <PlanIcon sx={{ color: '#FF669B', fontSize: 26 }} />
                                                )}
                                            </Box>

                                            {/* Plan name */}
                                            <Typography
                                                sx={{
                                                    fontSize: { xs: '32px', md: '34px' },
                                                    fontWeight: 600,
                                                    color: '#000453',
                                                    letterSpacing: { xs: '-1.28px', md: '-0.7px' },
                                                    lineHeight: { xs: '32px', md: 1.1 },
                                                    mb: 1,
                                                }}
                                            >
                                                {displayName}
                                            </Typography>

                                            {/* Description */}
                                            <Typography
                                                sx={{
                                                    fontSize: { xs: '12px', md: '14px' },
                                                    color: '#616063',
                                                    lineHeight: { xs: '16px', md: 1.5 },
                                                    letterSpacing: { xs: '-0.24px', md: 'normal' },
                                                    mb: 2,
                                                    minHeight: { xs: 'auto', md: 42 },
                                                }}
                                            >
                                                {planData.description || ''}
                                            </Typography>

                                            {/* Price */}
                                            <Box sx={{ display: 'flex', alignItems: 'baseline', gap: '4px', mb: 0.5 }}>
                                                <Typography
                                                    sx={{
                                                        fontSize: { xs: '24px', md: '32px' },
                                                        fontWeight: 600,
                                                        color: '#000453',
                                                        letterSpacing: { xs: '-0.96px', md: '-0.6px' },
                                                        lineHeight: { xs: '24px', md: 'normal' },
                                                    }}
                                                >
                                                    £{price}
                                                </Typography>
                                                <Typography
                                                    sx={{
                                                        fontSize: { xs: '12px', md: '14px' },
                                                        color: '#616063',
                                                        fontWeight: 400,
                                                        letterSpacing: { xs: '-0.48px', md: 'normal' },
                                                    }}
                                                >
                                                    {billingCycle === 'annually' ? '/year' : '/month'}
                                                </Typography>
                                            </Box>
                                            {discount && (
                                                <Typography
                                                    sx={{
                                                        fontSize: '12px',
                                                        color: '#FF669B',
                                                        fontWeight: 600,
                                                        mb: 0.5,
                                                    }}
                                                >
                                                    {discount.value}% off applied
                                                </Typography>
                                            )}
                                        </Box>

                                        {/* Bottom white section — benefits and button */}
                                        <Box
                                            sx={{
                                                backgroundColor: '#fff',
                                                p: 3,
                                                flexGrow: 1,
                                                display: 'flex',
                                                flexDirection: 'column',
                                            }}
                                        >
                                            {/* Benefits */}
                                            <Box sx={{ flexGrow: 1, mb: 3 }}>
                                                {(planData.benefits || []).map((benefit, idx) => {
                                                    const isBold = benefit.startsWith('**') && benefit.endsWith('**');
                                                    const text = isBold ? benefit.slice(2, -2) : benefit;
                                                    return (
                                                        <Box
                                                            key={idx}
                                                            sx={{
                                                                display: 'flex',
                                                                alignItems: 'flex-start',
                                                                gap: '8px',
                                                                mb: 1,
                                                            }}
                                                        >
                                                            <CheckCircleOutlineIcon
                                                                sx={{
                                                                    color: '#FF669B',
                                                                    fontSize: 18,
                                                                    mt: '1px',
                                                                    flexShrink: 0,
                                                                }}
                                                            />
                                                            <Typography
                                                                sx={{
                                                                    fontSize: '13px',
                                                                    color: '#333',
                                                                    lineHeight: 1.5,
                                                                    fontWeight: isBold ? 700 : 400,
                                                                }}
                                                            >
                                                                {text}
                                                            </Typography>
                                                        </Box>
                                                    );
                                                })}
                                            </Box>

                                            {/* Select plan button */}
                                            <Button
                                                variant="contained"
                                                fullWidth
                                                disabled={isMultiUser}
                                                onClick={() => handleSelectPlan(plan)}
                                                sx={{
                                                    backgroundColor: '#FF669B',
                                                    color: '#fff',
                                                    borderRadius: '50px',
                                                    textTransform: 'none',
                                                    fontWeight: 500,
                                                    fontSize: '15px',
                                                    height: { xs: '30.75px', md: '37px' },
                                                    py: 0,
                                                    boxShadow: 'none',
                                                    '&:hover': {
                                                        backgroundColor: '#e55588',
                                                        boxShadow: 'none',
                                                    },
                                                    '&.Mui-disabled': {
                                                        backgroundColor: 'rgba(255,102,155,0.4)',
                                                        color: '#fff',
                                                    },
                                                }}
                                            >
                                                Select plan
                                            </Button>
                                        </Box>
                                    </Box>
                                );
                            })}
                    </Box>

                    {/* Enterprise footer */}
                    <Box sx={{ textAlign: 'center', mt: '18px' }}>
                        <Typography
                            sx={{
                                fontSize: { xs: '13px', md: '18px' },
                                fontWeight: 400,
                                color: '#616063',
                                letterSpacing: { xs: '-0.26px', md: '-0.36px' },
                                lineHeight: { xs: '20px', md: '28px' },
                            }}
                        >
                            Need flexible users, advanced permissions or bespoke licensing? Talk to us about our{' '}
                            <Box
                                component="span"
                                onClick={() => setEnterpriseDialogOpen(true)}
                                sx={{
                                    color: '#FF669B',
                                    textDecoration: 'underline',
                                    textDecorationSkipInk: 'none',
                                    cursor: 'pointer',
                                }}
                            >
                                Enterprise plan
                            </Box>
                        </Typography>
                    </Box>
                </Box>
            </Box>

            {/* Enterprise dialog */}
            <Dialog
                open={enterpriseDialogOpen}
                onClose={() => setEnterpriseDialogOpen(false)}
                maxWidth={false}
                PaperProps={{
                    sx: {
                        borderRadius: '28px',
                        p: { xs: '20px', sm: '28px' },
                        width: '750px',
                        maxWidth: 'calc(100% - 32px)',
                        m: 2,
                        textAlign: 'center',
                    },
                }}
            >
                <Typography
                    sx={{
                        fontSize: { xs: '24px', sm: '36px' },
                        fontWeight: 600,
                        color: '#000453',
                        letterSpacing: { xs: '-0.96px', sm: '-1.44px' },
                        lineHeight: { xs: '24px', sm: '36px' },
                        mb: { xs: '16px', sm: '28px' },
                    }}
                >
                    Excellent choice
                </Typography>
                <Typography
                    sx={{
                        fontSize: { xs: '14px', sm: '20px' },
                        fontWeight: 400,
                        color: '#616063',
                        letterSpacing: { xs: '-0.4px', sm: '-0.8px' },
                        lineHeight: { xs: '22px', sm: '30px' },
                        mb: { xs: '20px', sm: '28px' },
                    }}
                >
                    Let's explore this further. Please email us at{' '}
                    <Box
                        component="a"
                        href="mailto:hello@cetravets.com"
                        target="_blank"
                        rel="noreferrer"
                        sx={{ fontWeight: 600, color: '#616063', textDecoration: 'none' }}
                    >
                        hello@cetravets.com
                    </Box>{' '}
                    detailing the type of enterprise package you're interested in. If preferred, we can schedule a
                    meeting to discuss the finer details.
                </Typography>
                <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                    <Button
                        onClick={() => setEnterpriseDialogOpen(false)}
                        sx={{
                            backgroundColor: '#FF669B',
                            color: '#fff',
                            borderRadius: '28px',
                            width: { xs: '160px', sm: '198px' },
                            height: { xs: '44px', sm: '52px' },
                            fontSize: { xs: '16px', sm: '20px' },
                            fontWeight: 500,
                            letterSpacing: '-0.8px',
                            textTransform: 'none',
                            '&:hover': { backgroundColor: '#e65588' },
                        }}
                    >
                        Close
                    </Button>
                </Box>
            </Dialog>
        </>
    );
};

export default OnboardPlanScreen;
